import { getData, postData, putData, deleteData } from "../lib/fetchApi";
import { BANNERS_URL } from "../constants/urls";
import { defaultLocale } from "../config";

const bannersService = {
    /**
     * Get paginated list of banners
     * @param {Object} params - Query parameters
     * @param {number} params.page - Page number (default 1)
     * @param {number} params.limit - Items per page (default 10)
     * @param {boolean} params.isActive - Filter by active status (optional)
     * @param {string} lang - Language code
     * @returns {Promise<{data: Array, total: number, page: number, limit: number}>}
     */
    getBannersList: (params = {}, lang = defaultLocale) => {
        const queryParams = new URLSearchParams();
        
        if (params.page) queryParams.append('page', params.page);
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.isActive !== undefined) queryParams.append('isActive', params.isActive);
        
        const url = `${BANNERS_URL.GET_LIST}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        return getData({ url, lang });
    },

    /**
     * Get active banner for current company (returns single banner for compatibility)
     * @param {string} lang - Language code
     * @returns {Promise<Object|null>} Active banner object or null
     * @deprecated Use getActiveBanners instead
     */
    getActiveBanner: (lang = defaultLocale) => {
        return getData({ url: BANNERS_URL.GET_ACTIVE, lang });
    },

    /**
     * Get active banners for current company
     * @param {string} lang - Language code
     * @returns {Promise<Array>} Array of active banner objects with multilingual image_url
     */
    getActiveBanners: (lang = defaultLocale) => {
        return getData({ url: BANNERS_URL.GET_ACTIVE, lang });
    },

    /**
     * Get banner by ID
     * @param {number} id - Banner ID
     * @param {string} lang - Language code
     * @returns {Promise<Object>} Banner object
     */
    getBannerById: (id, lang = defaultLocale) => {
        return getData({ url: `${BANNERS_URL.GET_BY_ID}/${id}`, lang });
    },

    /**
     * Create new banner
     * @param {Object} bannerData - Banner data
     * @param {Array} bannerData.categories - Categories array
     * @param {Array} bannerData.brand - Brands array
     * @param {Array} bannerData.other - Other filters array
     * @param {Array} bannerData.fish_type - Fish types array
     * @param {Array} bannerData.fishing_type - Fishing types array
     * @param {string} bannerData.search_strings - Search string
     * @param {boolean} bannerData.is_test - Is test banner (optional, default true)
     * @param {string} lang - Language code
     * @returns {Promise<Object>} Created banner object
     */
    createBanner: (bannerData, lang = defaultLocale) => {
        return postData({ url: BANNERS_URL.CREATE, data: bannerData, lang });
    },

    /**
     * Update existing banner
     * @param {number} id - Banner ID
     * @param {Object} bannerData - Partial banner data to update
     * @param {string} lang - Language code
     * @returns {Promise<Object>} Updated banner object
     */
    updateBanner: (id, bannerData, lang = defaultLocale) => {
        return putData({ url: `${BANNERS_URL.UPDATE}/${id}`, data: bannerData, lang });
    },

    /**
     * Delete banner
     * @param {number} id - Banner ID
     * @param {string} lang - Language code
     * @returns {Promise<Object>} Success response
     */
    deleteBanner: (id, lang = defaultLocale) => {
        return deleteData({ url: `${BANNERS_URL.DELETE}/${id}`, lang });
    },

    /**
     * Upload banner image for specific language
     * @param {number} id - Banner ID
     * @param {File} imageFile - Image file to upload
     * @param {string} lang - Language code (e.g., 'en', 'uk', 'ru')
     * @returns {Promise<Object>} Response with imageUrl and updated banner
     */
    uploadBannerImage: async (id, imageFile, lang = defaultLocale) => {
        const formData = new FormData();
        formData.append('file', imageFile);
        
        const token = sessionStorage.getItem('jwt');
        const response = await fetch(`${BANNERS_URL.UPLOAD_IMAGE}/${id}?lang=${lang}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        });
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Upload failed');
        }
        
        return response.json();
    },

    /**
     * Delete banner image for specific language
     * @param {number} id - Banner ID
     * @param {string} lang - Language code
     * @returns {Promise<Object>} Success response
     */
    deleteBannerImage: async (id, lang) => {
        const token = sessionStorage.getItem('jwt');
        const response = await fetch(`${BANNERS_URL.UPLOAD_IMAGE}/${id}?lang=${lang}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Delete failed');
        }
        
        return response.json();
    },

    /**
     * Get banner image URL for specific language
     * @param {Object|string} imageUrl - Image URL object {lang: filename} or filename string
     * @param {string} lang - Language code
     * @returns {string|null} Full URL to the image or null
     */
    getBannerImageUrl: (imageUrl, lang = defaultLocale) => {
        if (!imageUrl) return null;
        
        // If imageUrl is a string (old format), return it directly
        if (typeof imageUrl === 'string') {
            return `${BANNERS_URL.GET_IMAGE}/${imageUrl}`;
        }
        
        // If imageUrl is an object (new format), get filename for specific language
        const filename = imageUrl[lang] || imageUrl['uk'] || imageUrl['en'] || Object.values(imageUrl)[0];
        return filename ? `${BANNERS_URL.GET_IMAGE}/${filename}` : null;
    }
};

export default bannersService;
